/**
 * JSONC Parser Utility
 *
 * Parses JSONC (JSON with Comments) from markdown code blocks.
 * Used to extract Value Flows schemas from Smart Asset template files.
 */

/**
 * Extract JSONC code block content from markdown
 *
 * @param markdown - The markdown content containing ```jsonc blocks
 * @returns The extracted JSONC content, or null if not found
 */
export function extractJsoncFromMarkdown(markdown: string): string | null {
  // Match ```jsonc code blocks (supports both ```jsonc and ```json)
  const jsoncBlockRegex = /```jsonc?\s*\n([\s\S]*?)```/;
  const match = markdown.match(jsoncBlockRegex);

  if (!match?.[1]) {
    return null;
  }

  return match[1].trim();
}

/**
 * Strip comments from JSONC content
 *
 * Removes:
 * - Single-line comments (// ...)
 * - Multi-line comments (/* ... *\/)
 *
 * @param jsonc - JSONC content with comments
 * @returns Clean JSON without comments
 */
export function stripJsoncComments(jsonc: string): string {
  // Remove single-line comments (// ...) but not URLs (http://)
  // Use negative lookbehind to avoid matching protocol slashes
  let result = jsonc.replace(/(?<!:)\/\/.*$/gm, "");

  // Remove multi-line comments (/* ... */)
  result = result.replace(/\/\*[\s\S]*?\*\//g, "");

  // Clean up any trailing commas before closing brackets/braces
  // This handles cases where removing a comment leaves a trailing comma
  result = result.replace(/,(\s*[}\]])/g, "$1");

  return result;
}

/**
 * Parse JSONC content into a JavaScript object
 *
 * @param jsonc - JSONC content (JSON with comments)
 * @returns Parsed JavaScript object
 * @throws Error if parsing fails
 */
export function parseJsonc<T = unknown>(jsonc: string): T {
  const cleanJson = stripJsoncComments(jsonc);

  try {
    return JSON.parse(cleanJson) as T;
  } catch (error) {
    throw new Error(
      `Failed to parse JSONC: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Extract and parse JSONC from markdown in one step
 *
 * @param markdown - Markdown content containing a ```jsonc block
 * @returns Parsed object, or null if no JSONC block found
 * @throws Error if JSONC parsing fails
 */
export function parseJsoncFromMarkdown<T = unknown>(markdown: string): T | null {
  const jsoncContent = extractJsoncFromMarkdown(markdown);

  if (!jsoncContent) {
    return null;
  }

  return parseJsonc<T>(jsoncContent);
}
